import React, { useEffect, useMemo, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import api, { mediaUrl, SITE_URL } from '../api/client';
import Seo from '../components/Seo';
import Breadcrumb from '../components/common/Breadcrumb';
import Gallery from '../components/product/Gallery';
import ContentBlocks from '../components/product/ContentBlocks';
import ProductSlider from '../components/product/ProductSlider';
import Stars from '../components/common/Stars';
import { PageLoader } from '../components/common/Spinner';
import { useCart } from '../context/CartContext';
import { useSettings } from '../context/SettingsContext';
import { useUI } from '../context/UIContext';
import { money, stripHtml } from '../utils/format';
import { IconHeart, IconHeartFilled, IconCheck, IconChevronDown, IconMinus, IconPlus, IconWhatsapp } from '../components/common/Icons';
import NotFound from './NotFound';

function Collapse({ title, children, open: initial = false }) {
  const [open, setOpen] = useState(initial);
  return (
    <div className="apb_collapse_container">
      <div className="apb_collapse_title_container" onClick={() => setOpen((o) => !o)} role="button" aria-expanded={open}>
        <div className="apb_collapse_title">{title}</div>
        <div className="apb_collapse_arrow_container"><div className={`apb_collapse_arrow ${open ? 'apb_collapse_arrow_active' : ''}`}><IconChevronDown width={16} height={16} /></div></div>
      </div>
      <div className="apb_collapse_transition" style={{ maxHeight: open ? 5000 : 0, overflow: 'hidden', transition: 'max-height .35s ease' }}>
        <div className="apb_collapse_content_container"><div className="apb_collapse_content rte">{children}</div></div>
      </div>
      <div className="apb_collapse_split_line" />
    </div>
  );
}

export default function Product() {
  const params_ = useParams();
  const slug = params_.productSlug || params_.slug;
  const navigate = useNavigate();
  const { add, toggleWish, inWishlist } = useCart();
  const { settings } = useSettings();
  const { toast } = useUI();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);
  const [selected, setSelected] = useState({});
  const [qty, setQty] = useState(1);
  const [imgIndex, setImgIndex] = useState(0);
  const symbol = settings.shop?.currencySymbol || '$';

  useEffect(() => {
    setLoading(true);
    setNotFound(false);
    api
      .get(`/products/slug/${slug}`)
      .then((r) => {
        setData(r);
        const first = {};
        (r.product.options || []).forEach((o) => {
          const v = r.product.variants.find((x) => x.stock > 0) || r.product.variants[0];
          first[o.name] = v?.options?.[o.name] || o.values[0];
        });
        setSelected(first);
        setQty(1);
        setImgIndex(0);
      })
      .catch((e) => setNotFound(e.status === 404))
      .finally(() => setLoading(false));
  }, [slug]);

  const product = data?.product;
  const variant = useMemo(() => {
    if (!product || !product.variants?.length) return null;
    return product.variants.find((v) => (product.options || []).every((o) => v.options?.[o.name] === selected[o.name])) || null;
  }, [product, selected]);

  useEffect(() => {
    if (variant?.image && product) {
      const idx = product.images.findIndex((im) => im.url === variant.image);
      if (idx >= 0) setImgIndex(idx);
    }
  }, [variant, product]);

  if (loading) return <PageLoader />;
  if (notFound || !product) return <NotFound />;

  const price = variant ? variant.price : product.price;
  const compare = variant ? variant.compareAtPrice : product.compareAtPrice;
  const stock = variant ? variant.stock : product.stock;
  const available = !product.trackStock || stock > 0;
  const hasOptions = product.options && product.options.length > 0;
  const missingVariant = hasOptions && !variant;
  const wished = inWishlist(product._id);
  const mainCat = product.categories?.[0];
  const base = (settings.seo?.siteUrl || SITE_URL || '').replace(/\/$/, '');

  const isValueAvailable = (optName, value) =>
    product.variants.some((v) => v.options?.[optName] === value && (product.options || []).every((o) => o.name === optName || !selected[o.name] || v.options?.[o.name] === selected[o.name]) && v.stock > 0);

  const cartItem = () => ({
    productId: product._id,
    title: product.title,
    slug: product.slug,
    image: variant?.image || product.images[0]?.url,
    price,
    compareAtPrice: compare,
    variantId: variant?._id,
    variantTitle: variant?.title,
    quantity: qty,
  });

  const addToCart = () => {
    if (missingVariant) return toast('Please select all options', 'error');
    if (!available) return toast('This item is sold out', 'error');
    add(cartItem());
    return toast('Added to cart');
  };
  const buyNow = () => {
    if (missingVariant || !available) return addToCart();
    add(cartItem(), false);
    return navigate('/checkout');
  };

  const jsonLd = {
    '@context': 'https://schema.org',
    '@type': 'Product',
    name: product.title,
    image: product.images.map((im) => `${base}${mediaUrl(im.url)}`),
    description: stripHtml(product.seo?.metaDescription || product.shortDescription || product.description).slice(0, 500),
    sku: variant?.sku || product.sku || undefined,
    brand: { '@type': 'Brand', name: product.vendor || settings.general?.siteName },
    ...(product.reviewCount > 0 ? { aggregateRating: { '@type': 'AggregateRating', ratingValue: Number(product.rating || 5).toFixed(1), reviewCount: product.reviewCount } } : {}),
    offers: product.variants.length
      ? product.variants.map((v) => ({ '@type': 'Offer', url: `${base}/products/${product.slug}`, priceCurrency: settings.shop?.currency || 'USD', price: v.price, availability: v.stock > 0 ? 'https://schema.org/InStock' : 'https://schema.org/OutOfStock', itemCondition: 'https://schema.org/NewCondition', sku: v.sku || undefined, name: v.title }))
      : { '@type': 'Offer', url: `${base}/products/${product.slug}`, priceCurrency: settings.shop?.currency || 'USD', price: product.price, availability: available ? 'https://schema.org/InStock' : 'https://schema.org/OutOfStock', itemCondition: 'https://schema.org/NewCondition' },
  };
  const faqLd = product.faqs?.length
    ? { '@context': 'https://schema.org', '@type': 'FAQPage', mainEntity: product.faqs.map((f) => ({ '@type': 'Question', name: f.question, acceptedAnswer: { '@type': 'Answer', text: stripHtml(f.answer) } })) }
    : null;

  const crumbs = [...(mainCat ? [{ label: mainCat.name, url: `/collections/${mainCat.slug}` }] : [{ label: 'Products', url: '/collections/all' }]), { label: product.title }];

  return (
    <>
      <Seo title={product.title} description={product.shortDescription || stripHtml(product.description)} image={product.images[0]?.url} type="product" seo={product.seo || {}} jsonLd={[jsonLd, faqLd].filter(Boolean)} breadcrumbs={[{ label: 'Home', url: '/' }, ...crumbs]} keywords={product.tags}>
        <meta property="og:price:amount" content={String(price)} />
        <meta property="og:price:currency" content={settings.shop?.currency || 'USD'} />
        <meta property="product:price:amount" content={String(price)} />
        <meta property="product:price:currency" content={settings.shop?.currency || 'USD'} />
        <meta property="product:availability" content={available ? 'in stock' : 'out of stock'} />
      </Seo>
      <Breadcrumb items={crumbs} className="hidden md:block" />
      <div className="automizely_page_builder_wrapper" style={{ '--apb--heading-color': '#333333', '--apb--description-color': '#444444', '--apb--button-color': '#FFFFFF', '--apb--bg-color': '#FFFFFF', '--apb--button-background': '#333333', '--apb--font-family': 'inherit' }}>
        <div className="apb_body">
          <div className="apb_layout">
            <div className="apb_reset">
              <div className="apb_product_detail_section container-fluid py-6 md:py-12">
                <div className="apb_product_detail_content apb_product_detail_media_pc_image_mobile_dot eg-pdp">
                  <Gallery images={product.images} activeIndex={imgIndex} onChange={setImgIndex} title={product.title} />
                  <div className="apb_product_detail_split_div" />
                  <div className="apb_product_detail_information_container eg-pdp-info">
                    <div className="apb_product_detail_information_title_container">
                      {product.vendor && <p className="text-sm text-color-secondary uppercase tracking-wide mb-1">{product.vendor}</p>}
                      <h1 className="apb_product_detail_information_title eg-pdp-title">{product.title}</h1>
                    </div>
                    {product.reviewCount > 0 && (
                      <div className="flex items-center gap-2 mb-2">
                        <Stars value={product.rating} size={14} />
                        <span className="text-sm text-color-secondary">{Number(product.rating).toFixed(1)} ({product.reviewCount} reviews)</span>
                      </div>
                    )}
                    <div className="apb_product_detail_price_container">
                      <div>
                        <span className="apb_product_detail_price">{money(price, symbol)}</span>
                        {compare > price && <span className="apb_product_detail_compare_price apb_product_detail_price">{money(compare, symbol)}</span>}
                        {compare > price && <span className="eg-badge eg-badge--sale ml-2 align-middle">Save {money(compare - price, symbol)}</span>}
                      </div>
                    </div>
                    {product.shippingNote && <p className="text-sm text-color-secondary mb-3">{product.shippingNote}</p>}
                    <form className="apb-product-form" onSubmit={(e) => { e.preventDefault(); addToCart(); }}>
                      <div className="apb_product_detail_options_container">
                        <div className="apb_product_detail_options_group">
                          {(product.options || []).map((o) => (
                            <div className="apb_product_detail_option_container button_options" key={o.name}>
                              <div className="apb_product_detail_option_description"><span>{o.name}: <strong>{selected[o.name]}</strong></span></div>
                              {o.values.map((v) => {
                                const sel = selected[o.name] === v;
                                const avail = isValueAvailable(o.name, v);
                                return (
                                  <label key={v} className={`apb_product_detail_button_variant ${sel ? 'active' : ''} ${avail ? '' : 'unavailable'}`} onClick={() => setSelected((s) => ({ ...s, [o.name]: v }))}>
                                    {v}
                                  </label>
                                );
                              })}
                            </div>
                          ))}
                        </div>
                      </div>
                      <div className="flex items-center gap-3 mb-4">
                        <div className="eg-qty eg-qty--lg">
                          <button type="button" onClick={() => setQty((q) => Math.max(1, q - 1))} aria-label="Decrease"><IconMinus width={12} height={12} /></button>
                          <input value={qty} onChange={(e) => setQty(Math.max(1, parseInt(e.target.value, 10) || 1))} aria-label="Quantity" />
                          <button type="button" onClick={() => setQty((q) => q + 1)} aria-label="Increase"><IconPlus width={12} height={12} /></button>
                        </div>
                        {product.trackStock && stock > 0 && stock <= (settings.shop?.lowStockThreshold || 5) && <span className="text-sm text-red-600">Only {stock} left!</span>}
                        {product.trackStock && stock > (settings.shop?.lowStockThreshold || 5) && <span className="text-sm text-green-700 flex items-center"><IconCheck width={14} height={14} className="mr-1" /> In stock</span>}
                      </div>
                      {available ? (
                        <div className="apb_product_detail_add_to_cart_container">
                          <button type="submit" className="apb_product_detail_action_btn apb_product_detail_add_to_cart apb_product_detail_second_btn eg-btn-cart">
                            <div className="apb_product_detail_add_to_cart_text">Add to cart</div>
                          </button>
                        </div>
                      ) : (
                        <div className="apb_product_detail_add_to_cart_container_sold_out"><button type="button" className="apb_product_detail_add_to_cart_sold_out" disabled><span className="apb_product_detail_add_to_cart_text_sold_out">Sold Out</span></button></div>
                      )}
                      {available && (
                        <div className="apb_buy_now mt-3">
                          <button type="button" className="shopify-payment-button__button shopify-payment-button__button--unbranded sf__btn sf__btn-primary w-full eg-btn-buy" onClick={buyNow}>Buy it now</button>
                        </div>
                      )}
                      <div className="flex items-center gap-4 mt-4 text-sm">
                        <button type="button" className="flex items-center hover:text-color-secondary" onClick={() => { toggleWish(product); toast(wished ? 'Removed from wishlist' : 'Added to wishlist'); }}>
                          {wished ? <IconHeartFilled width={16} height={16} className="mr-1.5" /> : <IconHeart width={16} height={16} className="mr-1.5" />}
                          {wished ? 'In wishlist' : 'Add to wishlist'}
                        </button>
                        {settings.general?.whatsapp && (
                          <a className="flex items-center hover:text-color-secondary" href={`https://wa.me/${settings.general.whatsapp.replace(/\D/g, '')}?text=${encodeURIComponent(`Hi, I'm interested in ${product.title} (${base}/products/${product.slug})`)}`} target="_blank" rel="noreferrer">
                            <IconWhatsapp width={16} height={16} className="mr-1.5" /> Ask on WhatsApp
                          </a>
                        )}
                      </div>
                    </form>
                    <div className="tab_right mt-6">
                      {product.description && (
                        <Collapse title="Description" open>
                          <div dangerouslySetInnerHTML={{ __html: product.description }} />
                        </Collapse>
                      )}
                      {product.specifications?.length > 0 && (
                        <Collapse title="Specification">
                          <table className="eg-spec-table">
                            <tbody>{product.specifications.map((s, i) => <tr key={i}><th>{s.key}</th><td>{s.value}</td></tr>)}</tbody>
                          </table>
                        </Collapse>
                      )}
                      {product.packageContents?.length > 0 && (
                        <Collapse title="What's in the Package">
                          <ul className="list-disc pl-5">{product.packageContents.map((s, i) => <li key={i}>{s}</li>)}</ul>
                        </Collapse>
                      )}
                      {product.faqs?.length > 0 && (
                        <Collapse title="FAQ">
                          {product.faqs.map((f, i) => (
                            <div key={i} className="mb-4"><p className="font-medium">{f.question}</p><div dangerouslySetInnerHTML={{ __html: f.answer }} /></div>
                          ))}
                        </Collapse>
                      )}
                      <Collapse title="Shipping & Returns">
                        <p>Free shipping on orders over {money(settings.shop?.freeShippingOver || 0, symbol)}. 30-day hassle-free returns. See our <Link to="/pages/shipping-policy" className="underline">shipping policy</Link> and <Link to="/pages/refund-policy" className="underline">refund policy</Link>.</p>
                      </Collapse>
                    </div>
                  </div>
                </div>
              </div>
            </div>
            <ContentBlocks blocks={product.contentBlocks} />
          </div>
        </div>
      </div>
      {data.related?.length > 0 && (
        <section className="sf-product__section recently-viewed">
          <div className="container-fluid section-my">
            <h2 className="text-2xl md:text-4xl text-center mb-8 md:mb-16">You may also like</h2>
            <ProductSlider products={data.related} columns={4} id="related" />
          </div>
        </section>
      )}
      {/* Mobile sticky add-to-cart */}
      <div className="eg-pdp-sticky md:hidden">
        <div className="flex-1 min-w-0">
          <div className="font-medium truncate text-sm">{product.title}</div>
          <div className="text-sm">{money(price, symbol)}{compare > price && <span className="line-through text-color-secondary ml-2">{money(compare, symbol)}</span>}</div>
        </div>
        <button type="button" className="sf__btn sf__btn-primary sf__btn-small" onClick={addToCart} disabled={!available}>{available ? 'Add to cart' : 'Sold out'}</button>
      </div>
    </>
  );
}
